import React, { useCallback, useEffect, useState } from "react";
import { ToggleButton, ToggleButtonGroup, CircularProgress, Autocomplete, TextField } from "@mui/material";
import { PieChart } from "@mui/x-charts/PieChart";
import { LineChart } from "@mui/x-charts/LineChart";
import dayjs from "dayjs";
import jsPDF from "jspdf";
import { autoTable } from "jspdf-autotable";
import InsightsOutlinedIcon from "@mui/icons-material/InsightsOutlined";
import EventBusyOutlinedIcon from "@mui/icons-material/EventBusyOutlined";
import PercentOutlinedIcon from "@mui/icons-material/PercentOutlined";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import dashboardService, { DashboardFiltros, AgrupamentoTemporal } from "../../services/dashboardService.ts";
import handleService from "../../services/apiService.ts";
import { useFeedback } from "../../contexts/FeedbackContext.tsx";
import { parseJwt } from "../../utils/token.util.ts";
import Button from "../../ui/Button.tsx";
import StatTile from "../../ui/StatTile.tsx";
import ErrorBanner from "../../ui/ErrorBanner.tsx";
import "../../styles/design-system.css";

interface ContagemPorNome {
  nome: string;
  quantidade: number;
}

interface ContagemPorStatus {
  status: string;
  quantidade: number;
}

interface ContagemPorDia {
  dia: string;
  quantidade: number;
}

interface DashboardData {
  dataInicial: string;
  dataFinal: string;
  totalCirurgias: number;
  totalCanceladas: number;
  taxaCancelamentoPercentual: number;
  porConvenio: ContagemPorNome[];
  porMedico: ContagemPorNome[];
  porHospital: ContagemPorNome[];
  porInstrumentador: ContagemPorNome[];
  porTipoCirurgia: ContagemPorNome[];
  porMotivoCancelamento: ContagemPorNome[];
  porStatus: ContagemPorStatus[];
  serieTemporal: ContagemPorDia[];
}

interface EntidadeOption {
  id: string;
  nome: string;
}

const STATUS_LABELS: Record<string, string> = {
  AGENDADA: "Agendada",
  REALIZADA: "Realizada",
  SEPARADO: "Separado",
  CANCELADA: "Cancelada",
  ABERTO: "Aberto",
};

const STATUS_COLORS: Record<string, string> = {
  AGENDADA: "#2563EB",
  REALIZADA: "#158A43",
  SEPARADO: "#F2760F",
  CANCELADA: "#D6322B",
  ABERTO: "#0C7C72",
};

const RANKING_LIMIT = 10;

// Este dashboard não usa MUI ThemeProvider (só o design system CSS próprio,
// ds-*), então o Autocomplete cai no tema claro padrão do MUI por default —
// estas overrides só tratam de layout (largura, borda, fundo); cor de
// texto/ícone fica de fora de propósito (ver nota abaixo). O CSS global
// (.ds-field>input,.ds-field>select em design-system.css) não alcança o
// <input> do Autocomplete de propósito (ele fica vários níveis abaixo do
// .ds-field, não é filho direto) — antes disso ser corrigido pro combinador
// ">", esse vazamento causava a borda dupla/padding errado.
// IMPORTANTE: não mexer em padding/altura/posição do endAdornment aqui. O
// MUI centraliza a seta/X com `top: 50%` + `transform: translate(0, -50%)`
// (funciona pra qualquer altura da caixa) e reserva paddingRight sozinho
// conforme quantos ícones existem — uma tentativa anterior de sobrescrever
// só o `top` (sem tocar no transform) e zerar o padding vertical do
// size="small" quebrou essa conta e fez os ícones vazarem pra fora da
// borda. Deixar o MUI calcular esse espaço é o que realmente funciona.
// Cor de texto/ícone (dark mode) NÃO fica aqui: um "sx" perde a disputa de
// especificidade contra o styled() interno do MUI de forma imprevisível
// (mesma especificidade dos dois lados, decide quem foi inserido por último
// no <head>) — ver a regra global com !important em design-system.css.
const filterAutocompleteSx = {
  minWidth: 190,
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",
    background: "var(--surface)",
    fontSize: 13,
    "& .MuiOutlinedInput-notchedOutline": { borderColor: "var(--border-strong)" },
    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "var(--border-strong)" },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "var(--blue)", borderWidth: "1px" },
  },
};

// Paper do dropdown (renderizado num Popper/portal fora da árvore da page,
// mas ainda sob o mesmo <html data-theme>, então as CSS vars resolvem certo).
// Não uso tokens de tema MUI ("background.paper"/"text.primary") porque sem
// ThemeProvider eles sempre caem no tema claro padrão do MUI, ignorando o
// dark mode real do app — só as CSS vars do design system acompanham o
// toggle de tema daqui.
const autocompletePopupSlotProps = {
  paper: {
    sx: {
      bgcolor: "var(--surface)",
      color: "var(--ink)",
      border: "1px solid var(--border)",
      boxShadow: "var(--shadow-md)",
      "& .MuiAutocomplete-option": {
        fontSize: 13,
        '&[aria-selected="true"]': { bgcolor: "var(--surface-3)" },
        "&.Mui-focused": { bgcolor: "var(--surface-2)" },
      },
      "& .MuiAutocomplete-noOptions": { color: "var(--ink-faint)", fontSize: 13 },
    },
  },
};

const getDefaultPeriodo = () => {
  const now = new Date();
  const primeiroDia = new Date(now.getFullYear(), now.getMonth(), 1);
  const ultimoDia = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  const fmt = (d: Date) => {
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };
  return { dataInicial: fmt(primeiroDia), dataFinal: fmt(ultimoDia) };
};

// Reduz uma lista já ordenada desc a no máximo RANKING_LIMIT itens, somando o
// restante em um bucket "Outros" — evita gráficos de barra ilegíveis quando há
// muitos médicos/instrumentadores cadastrados.
const foldTop = (data: ContagemPorNome[]): ContagemPorNome[] => {
  if (data.length <= RANKING_LIMIT) return data;
  const top = data.slice(0, RANKING_LIMIT);
  const outros = data.slice(RANKING_LIMIT).reduce((acc, item) => acc + item.quantidade, 0);
  return [...top, { nome: "Outros", quantidade: outros }];
};

// Monta e dispara o download de um CSV com todas as seções do dashboard já
// carregado — sem chamada de rede nova, só formata o que já está em memória.
const exportarCSV = (dados: DashboardData) => {
  const linhas: string[] = [];
  linhas.push("Resumo");
  linhas.push("Periodo,Total,Canceladas,Taxa de cancelamento (%)");
  linhas.push(`${dados.dataInicial} a ${dados.dataFinal},${dados.totalCirurgias},${dados.totalCanceladas},${dados.taxaCancelamentoPercentual.toFixed(1)}`);
  linhas.push("");

  const secaoRanking = (titulo: string, lista: ContagemPorNome[]) => {
    linhas.push(titulo);
    linhas.push("Nome,Quantidade");
    lista.forEach((item) => linhas.push(`"${item.nome.replace(/"/g, '""')}",${item.quantidade}`));
    linhas.push("");
  };

  secaoRanking("Por convenio", dados.porConvenio);
  secaoRanking("Por medico", dados.porMedico);
  secaoRanking("Por hospital", dados.porHospital);
  secaoRanking("Por instrumentador", dados.porInstrumentador);
  secaoRanking("Por tipo de cirurgia", dados.porTipoCirurgia);
  secaoRanking("Por motivo de cancelamento", dados.porMotivoCancelamento);

  linhas.push("Por status");
  linhas.push("Status,Quantidade");
  dados.porStatus.forEach((item) => linhas.push(`${STATUS_LABELS[item.status] || item.status},${item.quantidade}`));
  linhas.push("");

  linhas.push("Serie temporal");
  linhas.push("Dia,Quantidade");
  dados.serieTemporal.forEach((item) => linhas.push(`${item.dia},${item.quantidade}`));

  const blob = new Blob([linhas.join("\n")], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `dashboard-cirurgias_${dados.dataInicial}_a_${dados.dataFinal}.csv`;
  link.click();
  URL.revokeObjectURL(url);
};

const ChartCard = ({
  title,
  action,
  children,
}: {
  title: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}) => (
  <div className="ds-card ds-chart-card">
    <div className="ds-chart-card-head">
      <h3>{title}</h3>
      {action}
    </div>
    {children}
  </div>
);

const DashboardPage = () => {
  const { showFeedback } = useFeedback();

  const defaults = getDefaultPeriodo();
  const [dataInicial, setDataInicial] = useState(defaults.dataInicial);
  const [dataFinal, setDataFinal] = useState(defaults.dataFinal);
  const [dataInicialTemp, setDataInicialTemp] = useState(defaults.dataInicial);
  const [dataFinalTemp, setDataFinalTemp] = useState(defaults.dataFinal);
  const [periodoRapido, setPeriodoRapido] = useState<"MES" | "90DIAS" | "ANO" | null>("MES");

  const [medicoId, setMedicoId] = useState("");
  const [hospitalId, setHospitalId] = useState("");
  const [convenioId, setConvenioId] = useState("");
  const [instrumentadorId, setInstrumentadorId] = useState("");

  const [medicos, setMedicos] = useState<EntidadeOption[]>([]);
  const [hospitais, setHospitais] = useState<EntidadeOption[]>([]);
  const [convenios, setConvenios] = useState<EntidadeOption[]>([]);
  const [instrumentadores, setInstrumentadores] = useState<EntidadeOption[]>([]);

  const [agrupamento, setAgrupamento] = useState<AgrupamentoTemporal>("DIA");

  const [dados, setDados] = useState<DashboardData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [erro, setErro] = useState<string | null>(null);

  const [gerandoPdf, setGerandoPdf] = useState(false);

  useEffect(() => {
    const carregarListasDeFiltro = async () => {
      try {
        const [medicosResp, hospitaisResp, conveniosResp, instrumentadoresResp] = await Promise.all([
          handleService({ endpoint: "/medico" }),
          handleService({ endpoint: "/hospital" }),
          handleService({ endpoint: "/convenio" }),
          handleService({ endpoint: "/instrumentador" }),
        ]);
        setMedicos(medicosResp?.data || []);
        setHospitais(hospitaisResp?.data || []);
        setConvenios(conveniosResp?.data || []);
        setInstrumentadores(instrumentadoresResp?.data || []);
      } catch (error) {
        // Silencioso: falha aqui só significa que os selects de filtro ficam vazios.
      }
    };
    carregarListasDeFiltro();
  }, []);

  const filtros: DashboardFiltros = { medicoId, hospitalId, convenioId, instrumentadorId };

  const fetchDados = useCallback(async () => {
    setIsLoading(true);
    try {
      const result = await dashboardService.obterDashboardCirurgias(dataInicial, dataFinal, filtros, agrupamento);
      setDados(result);
      setErro(null);
    } catch (error) {
      setErro("Não foi possível carregar os dados do dashboard.");
      showFeedback("Erro ao carregar dados do dashboard", "error");
    } finally {
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataInicial, dataFinal, medicoId, hospitalId, convenioId, instrumentadorId, agrupamento, showFeedback]);

  useEffect(() => {
    fetchDados();
  }, [fetchDados]);

  const aplicarFiltro = () => {
    setDataInicial(dataInicialTemp);
    setDataFinal(dataFinalTemp);
    setPeriodoRapido(null);
  };

  const fmtISO = (d: Date) => {
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const aplicarPeriodoRapido = (tipo: "MES" | "90DIAS" | "ANO") => {
    const hoje = new Date();
    let inicio: Date;
    let fim: Date;
    if (tipo === "MES") {
      inicio = new Date(hoje.getFullYear(), hoje.getMonth(), 1);
      fim = new Date(hoje.getFullYear(), hoje.getMonth() + 1, 0);
    } else if (tipo === "90DIAS") {
      fim = hoje;
      inicio = new Date(hoje);
      inicio.setDate(inicio.getDate() - 90);
    } else {
      inicio = new Date(hoje.getFullYear(), 0, 1);
      fim = new Date(hoje.getFullYear(), 11, 31);
    }
    setDataInicial(fmtISO(inicio));
    setDataFinal(fmtISO(fim));
    setDataInicialTemp(fmtISO(inicio));
    setDataFinalTemp(fmtISO(fim));
    setPeriodoRapido(tipo);
  };

  // Barras horizontais 100% customizadas (sem eixo/gridlines do BarChart do MUI
  // X) — rótulo flutua à esquerda, valor exato flutua à direita, barra fina com
  // canto arredondado, largura proporcional ao maior valor da lista.
  const RankingBarList = ({ data, color = "var(--blue)" }: { data: ContagemPorNome[]; color?: string }) => {
    const folded = foldTop(data);
    if (folded.length === 0) {
      return <p style={{ color: "var(--ink-faint)", fontSize: 13.2 }}>Nenhuma cirurgia no período.</p>;
    }
    const max = Math.max(...folded.map((d) => d.quantidade), 1);
    return (
      <div>
        {folded.map((item) => (
          <div className="ds-rank-row" key={item.nome}>
            <span className="name" title={item.nome}>{item.nome}</span>
            <div className="ds-rank-track">
              <div className="ds-rank-fill" style={{ width: `${(item.quantidade / max) * 100}%`, background: color }} />
            </div>
            <span className="val">{item.quantidade}</span>
          </div>
        ))}
      </div>
    );
  };

  const SerieTemporalChart = ({ data, agrupamento }: { data: ContagemPorDia[]; agrupamento: AgrupamentoTemporal }) => {
    if (data.length === 0) {
      return <p style={{ color: "var(--ink-faint)", fontSize: 13.2 }}>Nenhuma cirurgia no período.</p>;
    }
    const dataset = data.map((d) => ({
      ...d,
      label: agrupamento === "MES" ? dayjs(d.dia).format("MM/YYYY") : dayjs(d.dia).format("DD/MM"),
    }));
    // Respiro no teto do gráfico (equivalente ao domain={[0, 'dataMax + 2']} do
    // Recharts) — sem isso a linha encosta na borda superior quando o valor
    // máximo do período coincide com o próprio teto calculado automaticamente.
    const maxQuantidade = Math.max(...dataset.map((d) => d.quantidade), 0);
    return (
      <LineChart
        dataset={dataset}
        xAxis={[{ scaleType: "band", dataKey: "label", disableLine: true, disableTicks: true }]}
        yAxis={[{
          disableLine: true,
          disableTicks: true,
          min: 0,
          max: maxQuantidade + 2,
          // tickMinStep força o espaçamento mínimo entre ticks a 1 unidade —
          // equivalente ao allowDecimals={false} do Recharts, evita ticks
          // fracionários (0.5, 1.5...) quando a faixa de valores é pequena.
          tickMinStep: 1,
        }]}
        series={[{
          dataKey: "quantidade",
          label: "Cirurgias",
          color: "#2563EB",
          curve: "monotoneX",
          showMark: true,
          area: true,
        }]}
        height={280}
        margin={{ left: 20, right: 20, top: 20, bottom: 40 }}
        slotProps={{ legend: { hidden: true } }}
        // Cor dos eixos (dark mode) não fica aqui: um "sx" perde a disputa de
        // especificidade contra o styled() interno do MUI de forma
        // imprevisível — ver a regra global com !important em design-system.css.
        sx={{
          "& .MuiAreaElement-root": { fillOpacity: 0.12 },
          "& .MuiLineElement-root": { strokeWidth: 2.5 },
        }}
      />
    );
  };

  const statusData = (dados?.porStatus || []).filter((s) => s.quantidade > 0);

  // Cores fixas (RGB 0-255) do documento — um PDF formal pra impressão/envio
  // não acompanha o tema claro/escuro do app; fica sempre em fundo branco,
  // independente do darkMode ativo na tela.
  const PDF_INK: [number, number, number] = [15, 26, 46];
  const PDF_INK_SOFT: [number, number, number] = [90, 100, 120];
  const PDF_INK_FAINT: [number, number, number] = [140, 148, 165];
  const PDF_BLUE: [number, number, number] = [37, 99, 235];
  const PDF_STRIPE: [number, number, number] = [244, 247, 252];
  const PDF_BORDER: [number, number, number] = [215, 220, 230];

  const fmtDataBr = (iso: string) => {
    const [ano, mes, dia] = iso.split("-");
    return `${dia}/${mes}/${ano}`;
  };

  // Documento gerado 100% com texto/tabelas reais (jsPDF + jspdf-autotable),
  // não mais uma sequência de screenshots (html2canvas) do dashboard — gera
  // um relatório de verdade: paginado, com cabeçalho/rodapé fixos e tabelas
  // que não cortam no meio entre páginas (comportamento padrão do autoTable).
  const exportarPDF = async () => {
    if (!dados) return;
    setGerandoPdf(true);
    try {
      const pdf = new jsPDF({ orientation: "p", unit: "mm", format: "a4" });
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 14;
      const topContentMargin = 20; // espaço reservado pro cabeçalho corrido nas páginas 2+
      const bottomMargin = 18; // espaço reservado pro rodapé em toda página

      const agora = new Date();
      const emissao = `${String(agora.getDate()).padStart(2, "0")}/${String(agora.getMonth() + 1).padStart(2, "0")}/${agora.getFullYear()} às ${String(agora.getHours()).padStart(2, "0")}:${String(agora.getMinutes()).padStart(2, "0")}`;
      const responsavel = (parseJwt(localStorage.getItem("token"))?.sub as string) || "—";
      const periodoLabel = `${fmtDataBr(dados.dataInicial)} a ${fmtDataBr(dados.dataFinal)}`;

      const filtrosAtivos = [
        medicoId && `Médico: ${medicos.find((m) => m.id === medicoId)?.nome}`,
        hospitalId && `Hospital: ${hospitais.find((h) => h.id === hospitalId)?.nome}`,
        convenioId && `Convênio: ${convenios.find((c) => c.id === convenioId)?.nome}`,
        instrumentadorId && `Instrumentador: ${instrumentadores.find((i) => i.id === instrumentadorId)?.nome}`,
      ].filter(Boolean) as string[];

      // ---------- Cabeçalho institucional (só na primeira página) ----------
      let y = margin;
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(16);
      pdf.setTextColor(...PDF_INK);
      pdf.text("ControlSIS", margin, y + 6);
      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(11);
      pdf.setTextColor(...PDF_INK_SOFT);
      pdf.text("Relatório de Gestão Cirúrgica", margin, y + 12.5);

      // Selo de emissão — canto superior direito
      const seloWidth = 72;
      const seloX = pageWidth - margin - seloWidth;
      pdf.setDrawColor(...PDF_BORDER);
      pdf.setLineWidth(0.25);
      pdf.roundedRect(seloX, y - 2, seloWidth, 14, 1.2, 1.2);
      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(7.5);
      pdf.setTextColor(...PDF_INK_SOFT);
      pdf.text(`Emitido em ${emissao}`, seloX + seloWidth / 2, y + 3.5, { align: "center" });
      pdf.text(`Responsável: ${responsavel}`, seloX + seloWidth / 2, y + 8.5, { align: "center", maxWidth: seloWidth - 4 });

      y += 18;
      pdf.setDrawColor(...PDF_BORDER);
      pdf.setLineWidth(0.4);
      pdf.line(margin, y, pageWidth - margin, y);
      y += 6;

      pdf.setFontSize(9.5);
      pdf.setTextColor(...PDF_INK_SOFT);
      pdf.text(`Período: ${periodoLabel}`, margin, y);
      y += 5;
      if (filtrosAtivos.length > 0) {
        pdf.text(`Filtros aplicados: ${filtrosAtivos.join("  ·  ")}`, margin, y);
        y += 5;
      }
      y += 3;

      // ---------- Sumário executivo (KPIs) ----------
      const realizadas = dados.porStatus.find((s) => s.status === "REALIZADA")?.quantidade || 0;
      autoTable(pdf, {
        startY: y,
        margin: { left: margin, right: margin, top: topContentMargin, bottom: bottomMargin },
        theme: "grid",
        head: [["Total de Cirurgias", "Realizadas", "Canceladas", "Taxa de Cancelamento"]],
        body: [[
          String(dados.totalCirurgias),
          String(realizadas),
          String(dados.totalCanceladas),
          `${dados.taxaCancelamentoPercentual.toFixed(1)}%`,
        ]],
        styles: { font: "helvetica", halign: "center", cellPadding: 4, lineColor: PDF_BORDER, lineWidth: 0.2 },
        headStyles: { fillColor: PDF_INK, textColor: 255, fontStyle: "bold", fontSize: 8.5 },
        bodyStyles: { fontStyle: "bold", fontSize: 15, textColor: PDF_INK },
      });
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      y = (pdf as any).lastAutoTable.finalY + 10;

      // ---------- Seções de tabela ----------
      const desenharTituloSecao = (titulo: string, yPos: number) => {
        // Evita título de seção "órfão" sozinho no fim da página com a
        // tabela inteira empurrada pra próxima — sem espaço nem pro título
        // + começo da tabela, quebra a página antes de desenhar o título.
        if (yPos > pageHeight - bottomMargin - 30) {
          pdf.addPage();
          yPos = topContentMargin;
        }
        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(11.5);
        pdf.setTextColor(...PDF_INK);
        pdf.text(titulo, margin, yPos);
        return yPos + 5;
      };

      // Uma linha por item + coluna de "% do total" — sem o corte "Outros" que
      // a versão em tela usa (RANKING_LIMIT/foldTop): num relatório formal
      // paginado a lista completa é mais correta do que truncar.
      const desenharTabelaRanking = (titulo: string, colunaNome: string, lista: ContagemPorNome[], yPos: number) => {
        let yy = desenharTituloSecao(titulo, yPos);
        if (lista.length === 0) {
          pdf.setFont("helvetica", "normal");
          pdf.setFontSize(9.5);
          pdf.setTextColor(...PDF_INK_FAINT);
          pdf.text("Nenhuma cirurgia no período.", margin, yy + 2);
          return yy + 10;
        }
        const total = lista.reduce((acc, item) => acc + item.quantidade, 0);
        autoTable(pdf, {
          startY: yy,
          margin: { left: margin, right: margin, top: topContentMargin, bottom: bottomMargin },
          theme: "striped",
          head: [[colunaNome, "Quantidade", "% do total"]],
          body: lista.map((item) => [
            item.nome,
            String(item.quantidade),
            total > 0 ? `${((item.quantidade / total) * 100).toFixed(1)}%` : "0%",
          ]),
          styles: { font: "helvetica", fontSize: 9, cellPadding: 3, lineColor: PDF_BORDER, lineWidth: 0.1 },
          headStyles: { fillColor: PDF_BLUE, textColor: 255, fontStyle: "bold", fontSize: 8.5 },
          alternateRowStyles: { fillColor: PDF_STRIPE },
          columnStyles: { 1: { halign: "right", cellWidth: 28 }, 2: { halign: "right", cellWidth: 28 } },
        });
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return (pdf as any).lastAutoTable.finalY + 10;
      };

      // Distribuição por Status
      y = desenharTituloSecao("Distribuição por Status", y);
      const totalStatus = dados.porStatus.reduce((acc, s) => acc + s.quantidade, 0);
      autoTable(pdf, {
        startY: y,
        margin: { left: margin, right: margin, top: topContentMargin, bottom: bottomMargin },
        theme: "striped",
        head: [["Status", "Quantidade", "% do total"]],
        body: dados.porStatus.map((s) => [
          STATUS_LABELS[s.status] || s.status,
          String(s.quantidade),
          totalStatus > 0 ? `${((s.quantidade / totalStatus) * 100).toFixed(1)}%` : "0%",
        ]),
        styles: { font: "helvetica", fontSize: 9, cellPadding: 3, lineColor: PDF_BORDER, lineWidth: 0.1 },
        headStyles: { fillColor: PDF_BLUE, textColor: 255, fontStyle: "bold", fontSize: 8.5 },
        alternateRowStyles: { fillColor: PDF_STRIPE },
        columnStyles: { 1: { halign: "right", cellWidth: 28 }, 2: { halign: "right", cellWidth: 28 } },
      });
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      y = (pdf as any).lastAutoTable.finalY + 10;

      // Volume por Médico / Equipe Cirúrgica
      y = desenharTabelaRanking("Volume por Médico", "Médico", dados.porMedico, y);
      y = desenharTabelaRanking("Volume por Instrumentador (Equipe Cirúrgica)", "Instrumentador", dados.porInstrumentador, y);

      // Volume por Hospital e Convênio
      y = desenharTabelaRanking("Volume por Hospital", "Hospital", dados.porHospital, y);
      y = desenharTabelaRanking("Volume por Convênio", "Convênio", dados.porConvenio, y);

      // Tipos de Cirurgia mais frequentes
      desenharTabelaRanking("Tipos de Cirurgia mais Frequentes", "Tipo de Cirurgia", dados.porTipoCirurgia, y);

      // ---------- Cabeçalho corrido (pág. 2+) e rodapé (todas as páginas) ----------
      const totalPaginas = pdf.internal.getNumberOfPages();
      for (let i = 1; i <= totalPaginas; i++) {
        pdf.setPage(i);

        if (i > 1) {
          pdf.setFont("helvetica", "bold");
          pdf.setFontSize(8.5);
          pdf.setTextColor(...PDF_INK_SOFT);
          pdf.text("ControlSIS — Relatório de Gestão Cirúrgica", margin, 10);
          pdf.setFont("helvetica", "normal");
          pdf.setTextColor(...PDF_INK_FAINT);
          pdf.text(periodoLabel, pageWidth - margin, 10, { align: "right" });
          pdf.setDrawColor(...PDF_BORDER);
          pdf.setLineWidth(0.3);
          pdf.line(margin, 13, pageWidth - margin, 13);
        }

        pdf.setDrawColor(...PDF_BORDER);
        pdf.setLineWidth(0.3);
        pdf.line(margin, pageHeight - 13, pageWidth - margin, pageHeight - 13);
        pdf.setFont("helvetica", "normal");
        pdf.setFontSize(7);
        pdf.setTextColor(...PDF_INK_FAINT);
        pdf.text(
          "Documento contém dados sensíveis de saúde protegidos pela LGPD (Lei 13.709/2018) — uso restrito e confidencial.",
          margin,
          pageHeight - 8
        );
        pdf.text(`Página ${i} de ${totalPaginas}`, pageWidth - margin, pageHeight - 8, { align: "right" });
      }

      pdf.save(`relatorio-gestao-cirurgica_${dados.dataInicial}_a_${dados.dataFinal}.pdf`);
    } catch (error) {
      showFeedback("Erro ao gerar PDF", "error");
    } finally {
      setGerandoPdf(false);
    }
  };

  return (
    <div className="ds">
      <div className="ds-page-head">
        <div>
          <h1>Dashboard</h1>
          <p>Visão consolidada das cirurgias no período selecionado.</p>
        </div>
        <div className="ds-page-head-actions">
          <div className="ds-view-toggle">
            <button type="button" className={periodoRapido === "MES" ? "active" : ""} onClick={() => aplicarPeriodoRapido("MES")}>
              Este mês
            </button>
            <button type="button" className={periodoRapido === "90DIAS" ? "active" : ""} onClick={() => aplicarPeriodoRapido("90DIAS")}>
              Últimos 90 dias
            </button>
            <button type="button" className={periodoRapido === "ANO" ? "active" : ""} onClick={() => aplicarPeriodoRapido("ANO")}>
              Ano
            </button>
          </div>
          {dados && (
            <>
              <Button variant="outline" size="sm" icon={<DownloadOutlinedIcon fontSize="small" />} onClick={() => exportarCSV(dados)}>
                CSV
              </Button>
              <Button
                variant="outline"
                size="sm"
                icon={gerandoPdf ? <CircularProgress size={14} /> : <PictureAsPdfOutlinedIcon fontSize="small" />}
                onClick={exportarPDF}
                disabled={gerandoPdf}
              >
                {gerandoPdf ? "Gerando..." : "PDF"}
              </Button>
            </>
          )}
        </div>
      </div>

      <div className="ds-toolbar">
        <div className="ds-field">
          <label>Data inicial</label>
          <input type="date" value={dataInicialTemp} onChange={(e) => { setDataInicialTemp(e.target.value); setPeriodoRapido(null); }} />
        </div>
        <div className="ds-field">
          <label>Data final</label>
          <input type="date" value={dataFinalTemp} min={dataInicialTemp} onChange={(e) => { setDataFinalTemp(e.target.value); setPeriodoRapido(null); }} />
        </div>
        <div className="ds-field">
          <label>Médico</label>
          <Autocomplete
            size="small"
            options={medicos}
            getOptionLabel={(o) => o.nome}
            isOptionEqualToValue={(o, v) => o.id === v.id}
            value={medicos.find((m) => m.id === medicoId) || null}
            onChange={(_, novo) => setMedicoId(novo?.id || "")}
            sx={filterAutocompleteSx}
            slotProps={autocompletePopupSlotProps}
            renderInput={(params) => <TextField {...params} placeholder="Todos" />}
          />
        </div>
        <div className="ds-field">
          <label>Hospital</label>
          <Autocomplete
            size="small"
            options={hospitais}
            getOptionLabel={(o) => o.nome}
            isOptionEqualToValue={(o, v) => o.id === v.id}
            value={hospitais.find((h) => h.id === hospitalId) || null}
            onChange={(_, novo) => setHospitalId(novo?.id || "")}
            sx={filterAutocompleteSx}
            slotProps={autocompletePopupSlotProps}
            renderInput={(params) => <TextField {...params} placeholder="Todos" />}
          />
        </div>
        <div className="ds-field">
          <label>Convênio</label>
          <Autocomplete
            size="small"
            options={convenios}
            getOptionLabel={(o) => o.nome}
            isOptionEqualToValue={(o, v) => o.id === v.id}
            value={convenios.find((c) => c.id === convenioId) || null}
            onChange={(_, novo) => setConvenioId(novo?.id || "")}
            sx={filterAutocompleteSx}
            slotProps={autocompletePopupSlotProps}
            renderInput={(params) => <TextField {...params} placeholder="Todos" />}
          />
        </div>
        <div className="ds-field">
          <label>Instrumentador</label>
          <Autocomplete
            size="small"
            options={instrumentadores}
            getOptionLabel={(o) => o.nome}
            isOptionEqualToValue={(o, v) => o.id === v.id}
            value={instrumentadores.find((i) => i.id === instrumentadorId) || null}
            onChange={(_, novo) => setInstrumentadorId(novo?.id || "")}
            sx={filterAutocompleteSx}
            slotProps={autocompletePopupSlotProps}
            renderInput={(params) => <TextField {...params} placeholder="Todos" />}
          />
        </div>
        <div className="ds-toolbar-filters">
          <Button variant="primary" size="sm" onClick={aplicarFiltro}>Filtrar</Button>
        </div>
      </div>

      {isLoading ? (
        <div className="ds-card" style={{ padding: 40, textAlign: "center", color: "var(--ink-faint)" }}>Carregando...</div>
      ) : erro ? (
        <>
          <ErrorBanner message={erro} />
          <Button variant="outline" size="sm" onClick={fetchDados}>Tentar novamente</Button>
        </>
      ) : !dados ? null : (
        <div>
          <div className="ds-stat-grid" style={{ gridTemplateColumns: "repeat(3, 1fr)" }}>
            <StatTile
              label="Total de cirurgias no período"
              value={dados.totalCirurgias}
              icon={<InsightsOutlinedIcon fontSize="small" />}
              color="blue"
            />
            <StatTile
              label="Cirurgias canceladas"
              value={dados.totalCanceladas}
              icon={<EventBusyOutlinedIcon fontSize="small" />}
              color="red"
            />
            <StatTile
              label="Taxa de cancelamento"
              value={`${dados.taxaCancelamentoPercentual.toFixed(1)}%`}
              icon={<PercentOutlinedIcon fontSize="small" />}
              color="orange"
            />
          </div>

          <div className="ds-grid-2" style={{ marginBottom: 16 }}>
            <ChartCard title="Distribuição por status">
              {statusData.length === 0 ? (
                <p style={{ color: "var(--ink-faint)", fontSize: 13.2 }}>Nenhuma cirurgia no período.</p>
              ) : (
                <div className="ds-donut-wrap">
                  <div className="ds-donut-chart">
                    <PieChart
                      series={[
                        {
                          data: statusData.map((s) => ({
                            id: s.status,
                            value: s.quantidade,
                            label: STATUS_LABELS[s.status] || s.status,
                            color: STATUS_COLORS[s.status],
                          })),
                          innerRadius: 58,
                          outerRadius: 88,
                          paddingAngle: 2,
                          cornerRadius: 3,
                          highlightScope: { faded: "global", highlighted: "item" },
                        },
                      ]}
                      height={220}
                      width={220}
                      margin={{ top: 0, right: 0, bottom: 0, left: 0 }}
                      slotProps={{ legend: { hidden: true } }}
                    />
                    <div className="ds-donut-center">
                      <div className="value">{dados.totalCirurgias}</div>
                      <div className="label">cirurgias</div>
                    </div>
                  </div>
                  <div className="ds-donut-legend">
                    {statusData.map((s) => (
                      <div className="ds-donut-legend-item" key={s.status}>
                        <span className="dot" style={{ background: STATUS_COLORS[s.status] }} />
                        <span className="name">{STATUS_LABELS[s.status] || s.status}</span>
                        <span className="value">{s.quantidade}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </ChartCard>
            <ChartCard title="Cirurgias por convênio">
              <RankingBarList data={dados.porConvenio} color="var(--blue)" />
            </ChartCard>
          </div>

          <div className="ds-grid-2" style={{ marginBottom: 16 }}>
            <ChartCard title="Cirurgias por médico"><RankingBarList data={dados.porMedico} color="var(--teal)" /></ChartCard>
            <ChartCard title="Cirurgias por hospital"><RankingBarList data={dados.porHospital} color="var(--blue)" /></ChartCard>
          </div>

          <div className="ds-grid-2" style={{ marginBottom: 16 }}>
            <ChartCard title="Cirurgias por instrumentador"><RankingBarList data={dados.porInstrumentador} color="var(--orange)" /></ChartCard>
            <ChartCard title="Cirurgias por tipo de cirurgia"><RankingBarList data={dados.porTipoCirurgia} color="var(--navy-2)" /></ChartCard>
          </div>

          <ChartCard
            title="Cirurgias por período"
            action={
              <ToggleButtonGroup
                size="small"
                exclusive
                value={agrupamento}
                onChange={(_, valor) => valor && setAgrupamento(valor)}
                sx={{
                  background: "var(--surface-2)",
                  border: "1px solid var(--border)",
                  borderRadius: "999px",
                  padding: "3px",
                  "& .MuiToggleButton-root": {
                    color: "var(--ink-soft)",
                    border: "none",
                    borderRadius: "999px !important",
                    textTransform: "none",
                    fontWeight: 700,
                    fontSize: 12.6,
                    padding: "5px 14px",
                  },
                  "& .MuiToggleButton-root:hover": {
                    backgroundColor: "var(--surface-3)",
                    color: "var(--ink)",
                  },
                  "& .MuiToggleButton-root.Mui-selected": {
                    color: "var(--ink)",
                    backgroundColor: "var(--surface)",
                    boxShadow: "var(--shadow-sm)",
                  },
                  "& .MuiToggleButton-root.Mui-selected:hover": {
                    backgroundColor: "var(--surface)",
                    color: "var(--ink)",
                  },
                }}
              >
                {/* Este dashboard não usa MUI ThemeProvider (só o design system CSS
                   próprio, ds-*), então cores como "text.secondary"/"divider" caem no
                   tema claro padrão do MUI e ficam ilegíveis no dark mode — por isso
                   as cores aqui são as CSS vars reais do app, não tokens de tema MUI. */}
                <ToggleButton value="DIA">Diário</ToggleButton>
                <ToggleButton value="MES">Mensal</ToggleButton>
              </ToggleButtonGroup>
            }
          >
            <SerieTemporalChart data={dados.serieTemporal} agrupamento={agrupamento} />
          </ChartCard>
        </div>
      )}
    </div>
  );
};

export default DashboardPage;
